/**
 * Shared definitions for team social links: type whitelist, URL building,
 * validation, and legacy-format conversion.
 * The backend SocialLinks::SanitizeService enforces the same rules —
 * keep both sides in sync when changing them.
 */

// A handle (after stripping a leading @) must fully match this pattern
// and contain at least one alphanumeric character
const HANDLE_PATTERN = /^[A-Za-z0-9._-]+$/
const HAS_ALNUM = /[A-Za-z0-9]/
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const PHONE_PATTERN = /^[\d\s()+-]+$/
const HAS_DIGIT = /\d/
const SAFE_SCHEMES = ['https:', 'http:', 'mailto:', 'tel:']

export const SOCIAL_LINKS_MAX_ENTRIES = 20
export const SOCIAL_LINK_VALUE_MAX_LENGTH = 500
export const SOCIAL_LINK_LABEL_MAX_LENGTH = 50

// kind: handle = @handle or platform URL; url = web address; text = email/phone; custom = free link (label required)
export const SOCIAL_LINK_TYPES = {
  instagram: { kind: 'handle', domains: ['instagram.com'], labelKey: 'type_instagram', placeholder: '@yourinstagram' },
  threads: { kind: 'handle', domains: ['threads.net', 'threads.com'], labelKey: 'type_threads', placeholder: '@yourthreads' },
  youtube: { kind: 'url', domains: ['youtube.com', 'youtu.be'], labelKey: 'type_youtube', placeholder: 'https://youtube.com/@yourchannel' },
  tiktok: { kind: 'handle', domains: ['tiktok.com'], labelKey: 'type_tiktok', placeholder: '@yourtiktok' },
  facebook: { kind: 'url', domains: ['facebook.com', 'fb.com'], labelKey: 'type_facebook', placeholder: 'https://facebook.com/yourpage' },
  line: { kind: 'handle', domains: ['line.me', 'lin.ee'], labelKey: 'type_line', placeholder: '@yourline' },
  x: { kind: 'handle', domains: ['x.com', 'twitter.com'], labelKey: 'type_x', placeholder: '@yourx' },
  website: { kind: 'url', domains: null, labelKey: 'type_website', placeholder: 'https://yourwebsite.com' },
  email: { kind: 'text', domains: null, labelKey: 'type_email', placeholder: 'contact@example.com' },
  phone: { kind: 'text', domains: null, labelKey: 'type_phone', placeholder: '0912-345-678' },
  custom: { kind: 'url', domains: null, labelKey: 'type_custom', placeholder: 'https://...' }
}

const HAS_PROTOCOL = /^[a-zA-Z][a-zA-Z0-9+.-]*:/
const SUPPORTED_LINK_PROTOCOL = /^https?:/i
// Protocol-relative prefixes: browsers normalize backslashes to slashes under
// special schemes, so //, /\, \/ and \\ all resolve to the next host —
// reject the whole class wherever a value/href could be host-relative
const PROTOCOL_RELATIVE_PREFIX = /^[/\\]{2}/

// Only http/https values parse; anything else (ftp:, javascript:, ...) is
// rejected here so the URL-or-handle rule can never classify it as a URL
const parseUrl = (value) => {
  // Protocol-relative host is rejected on both sides (Ruby's URI.parse
  // yields no host for it) — letting it through here would mean the editor
  // accepts what the server silently drops
  if (PROTOCOL_RELATIVE_PREFIX.test(value)) return null
  if (HAS_PROTOCOL.test(value) && !SUPPORTED_LINK_PROTOCOL.test(value)) return null
  const withProtocol = HAS_PROTOCOL.test(value) ? value : `https://${value}`
  try {
    return new URL(withProtocol)
  } catch {
    return null
  }
}

// Hostnames are compared lowercased; match when equal to a domain or ending
// with ".domain" (blocks instagram.com.evil.tld / evilinstagram.com)
const hostnameMatchesDomains = (hostname, domains) => {
  const host = hostname.toLowerCase()
  return domains.some(domain => host === domain || host.endsWith(`.${domain}`))
}

// URL-or-handle rule: treat the value as a URL when it parses (after
// prefixing https://) and its hostname matches the platform domain set
const asPlatformUrl = (value, domains) => {
  if (!domains) return null
  const url = parseUrl(value)
  if (url && hostnameMatchesDomains(url.hostname, domains)) return url
  return null
}

// Parses independently of parseUrl: mailto:/tel: are valid hrefs here even
// though they are never valid link *values*
const ensureSafeHref = (href) => {
  // Protocol-relative hrefs (//, /\, \/, \\) resolve to whatever scheme the
  // page uses and can exist in pre-sanitizer stored data — never render them
  if (PROTOCOL_RELATIVE_PREFIX.test(href)) return null
  const withProtocol = HAS_PROTOCOL.test(href) ? href : `https://${href}`
  try {
    const url = new URL(withProtocol)
    if (!SAFE_SCHEMES.includes(url.protocol.toLowerCase())) return null
    // Return the normalized href, not the raw input — a value that passed the
    // scheme check but embeds backslash tricks (https:///\evil.com) would
    // otherwise be re-parsed by the browser into a different host
    return url.href
  } catch {
    return null
  }
}

const stripAt = (value) => (value.startsWith('@') ? value.slice(1) : value)

/**
 * Raw value → full URL using per-platform templates. URL-shaped values are
 * used as-is (never concatenated into a profile template).
 * Returns null for any scheme outside https/http/mailto/tel.
 */
export const buildLinkUrl = (entry) => {
  if (!entry || typeof entry !== 'object') return null

  // Legacy prototype shape (url without value) renders via that url;
  // this fallback takes precedence over the skip rule
  if (typeof entry.value !== 'string' || entry.value.trim() === '') {
    if (typeof entry.url === 'string' && entry.url.trim() !== '') {
      return ensureSafeHref(entry.url)
    }
    return null
  }

  const config = SOCIAL_LINK_TYPES[entry.type]
  if (!config) return null
  const value = entry.value.trim()
  // Protocol-relative values would survive the https:// prefixing below
  // as "https:////host" — reject the whole class before any template applies
  if (PROTOCOL_RELATIVE_PREFIX.test(value)) return null

  if (entry.type === 'email') return ensureSafeHref(`mailto:${value}`)
  if (entry.type === 'phone') return ensureSafeHref(`tel:${value}`)

  if (config.kind === 'handle') {
    if (asPlatformUrl(value, config.domains)) {
      return ensureSafeHref(HAS_PROTOCOL.test(value) ? value : `https://${value}`)
    }
    const handle = stripAt(value)
    switch (entry.type) {
      case 'instagram': return ensureSafeHref(`https://instagram.com/${handle}`)
      case 'x': return ensureSafeHref(`https://x.com/${handle}`)
      case 'threads': return ensureSafeHref(`https://www.threads.com/@${handle}`)
      case 'tiktok': return ensureSafeHref(`https://www.tiktok.com/@${handle}`)
      case 'line':
        // @-prefixed means an official account (R/ti/p); otherwise a personal ID (ti/p/~)
        return ensureSafeHref(
          value.startsWith('@') ? `https://line.me/R/ti/p/${value}` : `https://line.me/ti/p/~${value}`
        )
      default: return null
    }
  }

  // URL types: keep the original value (prefix https:// when no protocol),
  // then pass it through the scheme whitelist
  return ensureSafeHref(HAS_PROTOCOL.test(value) ? value : `https://${value}`)
}

/**
 * Validate an entry by type. Returns an error i18n key, or null when valid.
 * Empty/whitespace-only values are treated as valid — they are dropped on
 * save instead of blocking it (takes precedence over the custom-label rule).
 */
export const validateLink = (entry) => {
  if (!entry || typeof entry !== 'object') return 'error_invalid'
  const config = SOCIAL_LINK_TYPES[entry.type]
  if (!config) return 'error_invalid'
  if (typeof entry.value !== 'string' || entry.value.trim() === '') return null

  const value = entry.value.trim()
  if (value.length > SOCIAL_LINK_VALUE_MAX_LENGTH) return 'error_too_long'

  if (entry.type === 'email') {
    return EMAIL_PATTERN.test(value) ? null : 'error_invalid_email'
  }
  if (entry.type === 'phone') {
    return PHONE_PATTERN.test(value) && HAS_DIGIT.test(value) ? null : 'error_invalid_phone'
  }
  if (entry.type === 'custom') {
    if (typeof entry.label !== 'string' || entry.label.trim() === '') return 'error_label_required'
  }

  if (config.kind === 'handle') {
    if (asPlatformUrl(value, config.domains)) return null
    const handle = stripAt(value)
    return HANDLE_PATTERN.test(handle) && HAS_ALNUM.test(handle) ? null : 'error_invalid_handle'
  }

  // URL types: must parse with a dotted hostname; youtube/facebook must
  // additionally match their platform domain set
  if (HAS_PROTOCOL.test(value) && !SUPPORTED_LINK_PROTOCOL.test(value)) return 'error_unsupported_protocol'
  const url = parseUrl(value)
  if (!url || !url.hostname.includes('.')) return 'error_invalid_url'
  if (config.domains && !hostnameMatchesDomains(url.hostname, config.domains)) return 'error_wrong_domain'
  return null
}

/**
 * Contact hash → renderable links for the public page (hero row + mobile
 * floating button). Defensive: non-array links is treated as empty,
 * non-object entries are skipped, and every href passes the scheme whitelist
 * via buildLinkUrl. Display text prefers custom label > value > legacy url.
 */
export const buildRenderableLinks = (contact) => {
  if (!contact) return []
  const normalized = convertLegacyContact(contact)
  if (!Array.isArray(normalized.links)) return []

  return normalized.links
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const url = buildLinkUrl(entry)
      if (!url) return null
      const display =
        (entry.type === 'custom' && typeof entry.label === 'string' && entry.label.trim()) ||
        (typeof entry.value === 'string' && entry.value.trim()) ||
        (typeof entry.url === 'string' && entry.url.trim()) || ''
      return { type: entry.type, display, url }
    })
    .filter(Boolean)
}

const LEGACY_FIELD_TYPES = [
  ['email', 'email'],
  ['phone', 'phone'],
  ['lineId', 'line'],
  ['instagram', 'instagram'],
  ['facebook', 'facebook']
]

/**
 * Legacy fixed-field format → links array. When a links key exists (even an
 * empty array) the fixed fields are ignored. The five legacy keys are removed
 * from the result; other keys (like ctaText) are preserved unchanged.
 */
export const convertLegacyContact = (content) => {
  if (!content || typeof content !== 'object') return { links: [] }

  const { email, phone, lineId, instagram, facebook, ...rest } = content

  if ('links' in content) {
    const { links, ...others } = rest
    return { ...others, links: Array.isArray(links) ? links : [] }
  }

  const links = LEGACY_FIELD_TYPES
    .filter(([field]) => typeof content[field] === 'string' && content[field].trim() !== '')
    .map(([field, type]) => ({ type, value: content[field] }))

  return { ...rest, links }
}
